"use client";

import { FontAwesomeIcon } from "@/component-library/components/media/iconography/FontAwesomeIcon";
import { FormEntry } from "@/component-library/components/user-input/forms/form-entry/FormEntry";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FC, useState } from "react";
import { DateInput } from "./DateField";
import {
    inquiryFormDateFieldContainerStyle,
    inquiryFormDateFieldInputStyle,
    inquiryFormOptionalLabelStyle,
    inquiryFormTimeSelectCaretStyle,
    inquiryFormTimeSelectEmptyStyle,
    inquiryFormTimeSelectStyle,
    inquiryFormViewingRowStyle,
} from "./PrivateOfficeInquiryForm.css";
import { getFirstViewingDate, getViewingTimesOn } from "./viewingTimeSlots";

interface ViewingDateFieldProps {
    /** Timezone-less YYYY-MM-DDTHH:mm, or "" when no viewing is wanted. */
    value: string;
    onChange: (value: string) => void;
}

/**
 * The optional "when would you like to visit?" field, shared by the private
 * office and virtual office inquiry forms.
 *
 * A date control and a dropdown of half-hour times rather than one
 * date-and-time control: a native time picker offers every minute, and iOS
 * ignores `step`, so a fixed list is the only way to keep the choice short on
 * every device.
 *
 * No default value: its presence is what tells operations the lead wants a
 * viewing, so a prefilled date would claim a slot nobody asked for. That makes
 * it the one field on either form that is routinely left blank, which is why
 * it carries a hint for its empty state and the dated fields around it do not.
 * Once either half is filled in, the other is required, so a lead cannot send
 * a date without a time and have it silently dropped.
 */
export const ViewingDateField: FC<ViewingDateFieldProps> = ({
    value,
    onChange,
}) => {
    const [initialDate = "", initialTime = ""] = value ? value.split("T") : [];
    const [date, setDate] = useState(initialDate);
    const [time, setTime] = useState(initialTime);

    const times = getViewingTimesOn(date);

    const update = (nextDate: string, nextTime: string) => {
        setDate(nextDate);
        setTime(nextTime);
        onChange(nextDate && nextTime ? `${nextDate}T${nextTime}` : "");
    };

    return (
        <FormEntry
            label={
                <>
                    Preferred viewing date and time{" "}
                    <span className={inquiryFormOptionalLabelStyle}>
                        (optional)
                    </span>
                </>
            }
        >
            <div className={inquiryFormViewingRowStyle}>
                <DateInput
                    type="date"
                    value={date}
                    // A time picked for another day may not be open on this
                    // one (it has passed, if this one is today).
                    onChange={(nextDate) =>
                        update(
                            nextDate,
                            getViewingTimesOn(nextDate).includes(time)
                                ? time
                                : ""
                        )
                    }
                    min={getFirstViewingDate()}
                    required={!!time}
                    emptyHint="Select a date"
                    aria-label="Preferred viewing date"
                />
                <div className={inquiryFormDateFieldContainerStyle}>
                    <select
                        value={time}
                        onChange={(e) => update(date, e.target.value)}
                        required={!!date}
                        aria-label="Preferred viewing time"
                        className={`${inquiryFormDateFieldInputStyle} ${inquiryFormTimeSelectStyle} ${
                            time ? "" : inquiryFormTimeSelectEmptyStyle
                        }`}
                    >
                        <option value="">Time</option>
                        {times.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <FontAwesomeIcon
                        icon={faCaretDown}
                        className={inquiryFormTimeSelectCaretStyle}
                    />
                </div>
            </div>
        </FormEntry>
    );
};
